const CHUNK_RELOAD_ATTEMPTED_KEY = 'chunk_reload_attempted';

// Keep the retry marker long enough for the restored route's lazy chunks to
// settle. If the refreshed deployment is still broken, the ErrorBoundary
// remains available instead of entering a reload loop.
export const CHUNK_RECOVERY_STABILITY_MS = 60_000;

const describeError = (error: unknown): string => {
  if (error instanceof Error) {
    return `${error.name} ${error.message}`;
  }

  if (typeof error === 'object' && error !== null) {
    const candidate = error as { name?: unknown; message?: unknown };
    const name = typeof candidate.name === 'string' ? candidate.name : '';
    const message = typeof candidate.message === 'string' ? candidate.message : '';
    return `${name} ${message}`;
  }

  return String(error ?? '');
};

export const isChunkLoadError = (error: unknown): boolean => {
  const description = describeError(error);

  return (
    /ChunkLoadError/i.test(description) ||
    /Loading (?:CSS )?chunk .+ failed/i.test(description) ||
    /Failed to fetch dynamically imported module/i.test(description) ||
    /Importing a module script failed/i.test(description)
  );
};

export const attemptChunkLoadRecovery = (error: unknown): boolean => {
  if (!isChunkLoadError(error)) {
    return false;
  }

  try {
    if (window.sessionStorage.getItem(CHUNK_RELOAD_ATTEMPTED_KEY)) {
      return false;
    }

    window.sessionStorage.setItem(CHUNK_RELOAD_ATTEMPTED_KEY, String(Date.now()));
  } catch {
    // Without durable per-tab storage there is no safe way to prevent a loop.
    return false;
  }

  window.location.reload();
  return true;
};

export const clearChunkLoadRecoveryAttempt = (): void => {
  try {
    window.sessionStorage.removeItem(CHUNK_RELOAD_ATTEMPTED_KEY);
  } catch {
    // Storage can be unavailable in restrictive browser modes; no cleanup is
    // necessary when no marker could be stored.
  }
};
